import { useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowDownTrayIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface PlanImageLightboxProps {
  imageUrl: string;
  title?: string;
  onClose: () => void;
  onDownload?: () => void;
  /**
   * When present, arrow keys and prev/next buttons cycle through a
   * gallery. Callers pass 0-based index + a callback that changes it;
   * the lightbox is agnostic to the underlying image list.
   */
  gallery?: {
    index: number;
    total: number;
    onIndexChange: (nextIndex: number) => void;
  };
}

/**
 * In-app fullscreen overlay for viewing a plan image at large size.
 * Replaces the previous `window.open(imageUrl)` behavior that took users
 * out of the workflow. Click outside the image or press Escape to close.
 */
export default function PlanImageLightbox({
  imageUrl,
  title,
  onClose,
  onDownload,
  gallery,
}: PlanImageLightboxProps) {
  const hasGallery = !!gallery && gallery.total > 1;
  const goPrev = () => gallery && gallery.onIndexChange((gallery.index - 1 + gallery.total) % gallery.total);
  const goNext = () => gallery && gallery.onIndexChange((gallery.index + 1) % gallery.total);

  // Arrow-key gallery nav + D-to-download stay in a manual keydown
  // listener because Radix's own Escape handling is enough for close;
  // it doesn't own the domain-specific shortcuts. Body-scroll-lock,
  // focus-trap on open, focus-restore on close, and aria-modal all
  // come from Radix Dialog now.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (hasGallery && (e.key === 'ArrowLeft')) { e.preventDefault(); goPrev(); return; }
      if (hasGallery && (e.key === 'ArrowRight')) { e.preventDefault(); goNext(); return; }
      if (onDownload && (e.key === 'd' || e.key === 'D')) {
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        e.preventDefault();
        onDownload();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDownload, hasGallery, gallery?.index, gallery?.total]);

  return (
    <Dialog.Root open onOpenChange={(next) => { if (!next) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed inset-0 z-[100] flex items-center justify-center p-6 focus:outline-none"
          onPointerDownOutside={(e) => {
            // Only close on click of the true backdrop — clicks on
            // Dialog.Content's own children bubble through Radix's
            // outside-click handler already.
            if ((e.target as HTMLElement).tagName === 'IMG') e.preventDefault();
          }}
        >
          <Dialog.Title className="sr-only">{title || 'Plan image'}</Dialog.Title>
          <Dialog.Description className="sr-only">
            Press Escape to close.{hasGallery ? ' Use the arrow keys to browse the gallery.' : ''}{onDownload ? ' Press D to download.' : ''}
          </Dialog.Description>

          {/* Toolbar */}
          <div className="absolute right-6 top-6 flex items-center gap-2">
            {onDownload && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDownload(); }}
                aria-label="Download"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
              </button>
            )}
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {title && (
            <p className="absolute left-6 top-6 max-w-[calc(100%-10rem)] truncate rounded-full bg-white/10 px-3 py-1.5 font-display text-sm text-white">
              {title}
            </p>
          )}

          {hasGallery && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                aria-label="Previous image"
                className="absolute left-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                aria-label="Next image"
                className="absolute right-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
              <span
                className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 font-mono text-xs text-white"
                aria-live="polite"
              >
                {gallery!.index + 1} / {gallery!.total}
              </span>
            </>
          )}

          <img
            src={imageUrl}
            alt={title || 'Plan'}
            className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Trigger a real file download in the browser (not a new tab). */
export async function downloadImage(url: string, suggestedName: string): Promise<void> {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(String(response.status));
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = suggestedName.replace(/[^\w.-]+/g, '_');
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Give the browser a moment to start the download before revoking.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch {
    // As a fallback, open in a new tab — better than nothing if CORS blocks us.
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
